<script>
import { useKeyboardEvents } from 'dashboard/composables/useKeyboardEvents';
import { REPLY_EDITOR_MODES, CHAR_LENGTH_WARNING } from './constants';
import EditorModeToggle from './EditorModeToggle.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import { createKeyboardEventsFromLabel } from 'dashboard/components/widgets/modal/constants';

export default {
  name: 'ReplyTopPanel',
  components: {
    EditorModeToggle,
    NextButton,
  },
  props: {
    mode: {
      type: String,
      default: REPLY_EDITOR_MODES.REPLY,
    },
    isMessageLengthReachingThreshold: {
      type: Boolean,
      default: () => false,
    },
    charactersRemaining: {
      type: Number,
      default: () => 0,
    },
    isPopout: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['setReplyMode', 'togglePopout'],
  setup(props, { emit }) {
    const setReplyMode = mode => {
      emit('setReplyMode', mode);
    };
    const handleReplyClick = () => {
      setReplyMode(REPLY_EDITOR_MODES.REPLY);
    };
    const handleNoteClick = () => {
      setReplyMode(REPLY_EDITOR_MODES.NOTE);
    };
    const handleModeToggle = () => {
      const newMode =
        props.mode === REPLY_EDITOR_MODES.REPLY
          ? REPLY_EDITOR_MODES.NOTE
          : REPLY_EDITOR_MODES.REPLY;
      setReplyMode(newMode);
    };
    const keyboardEvents = {
      ...createKeyboardEventsFromLabel('SWITCH_TO_PRIVATE_NOTE', {
        action: () => handleNoteClick(),
        allowOnFocusedInput: true,
      }),
      ...createKeyboardEventsFromLabel('SWITCH_TO_REPLY', {
        action: () => handleReplyClick(),
        allowOnFocusedInput: true,
      }),
    };
    useKeyboardEvents(keyboardEvents);

    return {
      handleModeToggle,
      handleReplyClick,
      handleNoteClick,
      REPLY_EDITOR_MODES,
    };
  },
  computed: {
    replyButtonClass() {
      return {
        'is-active': this.mode === REPLY_EDITOR_MODES.REPLY,
      };
    },
    noteButtonClass() {
      return {
        'is-active': this.mode === REPLY_EDITOR_MODES.NOTE,
      };
    },
    charLengthClass() {
      return this.charactersRemaining < 0 ? 'text-red-600' : 'text-slate-600';
    },
    characterLengthWarning() {
      return this.charactersRemaining < 0
        ? `${-this.charactersRemaining} ${CHAR_LENGTH_WARNING.NEGATIVE}`
        : `${this.charactersRemaining} ${CHAR_LENGTH_WARNING.UNDER_50}`;
    },
  },
};
</script>

<template>
  <div
    class="flex reply-to-panel justify-between h-[3.25rem] gap-2 ltr:pl-3 rtl:pr-3"
  >
    <EditorModeToggle
      :mode="mode"
      class="mt-3"
      @toggle-mode="handleModeToggle"
    />
    <div class="flex items-center mx-4 my-0">
      <div v-if="isMessageLengthReachingThreshold" class="text-xs">
        <span :class="charLengthClass">
          {{ characterLengthWarning }}
        </span>
      </div>
      <NextButton
        v-tooltip.top-end="
          isPopout
            ? $t('CONVERSATION.REPLYBOX.TIP_COLLAPSE_ICON')
            : $t('CONVERSATION.REPLYBOX.TIP_EXPAND_ICON')
        "
        ghost
        class="max-md:hidden text-n-slate-11 ltr:rounded-tr-[11px] rtl:rounded-tl-[11px]"
        :icon="isPopout ? 'i-lucide-minimize-2' : 'i-lucide-maximize-2'"
        @click="$emit('togglePopout')"
      />
    </div>
  </div>
</template>
